import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { type Sport } from '@/utils/jersey-mapping';

// Hack to support leagues.
type ExtendedSport = Sport | 'UCL' | 'PL';

const CDN_BASE_URL = 'https://tipmanpickemdev.blob.core.windows.net';

const UCL_TEAMS = [
  'AtleticoMadrid',
  'Barcelona',
  'BayernMunich',
  'Betis',
  'Blank',
  'BodoGlimt',
  'BorussiaDortmund',
  'ClubBrugge',
  'Como',
  'Feyenoord',
  'Galatasaray',
  'Inter',
  'Leipzig',
  'Lens',
  'Lille',
  'Napoli',
  'Porto',
  'PSG',
  'PSV',
  'RealMadrid',
  'Roma',
  'Shakhtar',
  'SlaviaPraha',
  'Sporting',
  'Stuttgart',
  'Villarreal',
];

const PREMIER_LEAGUE_TEAMS: string[] = [
  'Arsenal',
  'AstonVilla',
  'Bournemouth',
  'Brentford',
  'Brighton',
  'Chelsea',
  'Coventry',
  'CrystalPalace',
  'Everton',
  'Fulham',
  'Hull',
  'Ipswich',
  'Leeds',
  'Liverpool',
  'ManCity',
  'ManUtd',
  'Newcastle',
  'NottmForest',
  'Sunderland',
  'Tottenham',
];

const WORLD_CUP_TEAMS = [
  'Turkey',
  'Germany',
  'Brazil',
  'Bosnia',
  'Korea Republic',
  'Egypt',
  'Cape Verde',
  'Switzerland',
  'USA',
  'Scotland',
  'Panama',
  'Japan',
  'Austria',
  'Colombia',
  'Qatar',
  'Morocco',
  'Iran',
  'Uruguay',
  'Sweden',
  'Haiti',
  'Croatia',
  'Spain',
  'New Zealand',
  'Netherlands',
  'Uzbekistan',
  'Jordan',
  'Iraq',
  'Argentina',
  'Curacao',
  'Algeria',
  'Tunisia',
  'Mexico',
  'Ghana',
  'Norway',
  'Congo DR',
  'Canada',
  'Belgium',
  'Czechia',
  'England',
  'Saudia Arabia',
  'Paraguay',
  'Australia',
  'South Africa',
  'Portugal',
  'Senegal',
  'Ivory Coast',
  'Ecuador',
  'France',
];

const JERSEY_NUMBERS = Array.from({ length: 100 }, (_, i) => i.toString());

interface JerseySelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string) => void;
  fieldLabel?: string;
}

export function JerseySelectorModal({
  open,
  onOpenChange,
  onSelect,
  fieldLabel = 'Image',
}: JerseySelectorModalProps) {
  const [selectedSport, setSelectedSport] = useState<ExtendedSport>('NHL');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedProp, setSelectedProp] = useState<string>('');
  const [selectedNumber, setSelectedNumber] = useState<string>('blank');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);

  // Reset team selection when sport changes
  useEffect(() => {
    setSelectedTeam(selectedTeam);
  }, [selectedSport]);

  useEffect(() => {
    let url = '';

    if (selectedTeam) {
      if (selectedSport === 'WORLD_CUP') {
        url = `${CDN_BASE_URL}/jerseys/${selectedTeam}.svg`;
      } else {
        url = `${CDN_BASE_URL}/jerseys/${selectedTeam}.png`;
      }

      setPreviewUrl(url);
      setImageError(false);
    } else {
      setPreviewUrl('');
    }
  }, [selectedTeam, selectedNumber, selectedSport, selectedProp]);

  const handleSelect = () => {
    if (previewUrl) {
      onSelect(previewUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedSport('NHL');
    setSelectedTeam('');
    setSelectedNumber('blank');
    setSelectedProp('');
    setPreviewUrl('');
    setImageError(false);
    onOpenChange(false);
  };

  const formatTeamName = (teamKey: string) => {
    return teamKey.replace(/_/g, ' ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Select Jersey {fieldLabel && `for ${fieldLabel}`}</DialogTitle>
          <DialogDescription>Choose a sport, team, and optional jersey number</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sport-select">League</Label>
            <Select
              value={selectedSport}
              onValueChange={(value: ExtendedSport) => setSelectedSport(value)}
            >
              <SelectTrigger id="sport-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UCL">UCL</SelectItem>
                <SelectItem value="PL">PL</SelectItem>
                <SelectItem value="WORLD_CUP">WORLD CUP</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="team-select">Team</Label>
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger id="team-select">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {selectedSport === 'UCL' &&
                  UCL_TEAMS.map(team => (
                    <SelectItem key={team} value={team}>
                      {formatTeamName(team)}
                    </SelectItem>
                  ))}
                {selectedSport === 'PL' &&
                  PREMIER_LEAGUE_TEAMS.map(team => (
                    <SelectItem key={team} value={team}>
                      {formatTeamName(team)}
                    </SelectItem>
                  ))}
                {selectedSport === 'WORLD_CUP' &&
                  WORLD_CUP_TEAMS.map(team => (
                    <SelectItem key={team} value={team}>
                      {formatTeamName(team)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTeam && (
            <div className="space-y-2">
              <Label htmlFor="number-select">Jersey Number (Optional)</Label>
              <Select value={selectedNumber} onValueChange={setSelectedNumber}>
                <SelectTrigger id="number-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="blank">Blank (No Number)</SelectItem>
                  {JERSEY_NUMBERS.map(num => (
                    <SelectItem key={num} value={num}>
                      #{num}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* {selectedTeam && selectedSport === 'NFL' && (
            <p className="text-sm text-muted-foreground">
              NFL teams use a single jersey design for all players
            </p>
          )} */}

          {previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative w-full h-64 border rounded-md overflow-hidden bg-gray-50">
                {!imageError ? (
                  <img
                    src={previewUrl}
                    alt="Jersey preview"
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center text-sm text-muted-foreground">
                    Failed to load image
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground break-all">{previewUrl}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!previewUrl || imageError}>
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
